import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Send, CheckCircle2, XCircle, Database, Layers, BotMessageSquare } from "lucide-react";
import { api, formatApiError, fmtBytes } from "../lib/api";
import { GlassCard, PageHeader, QualityBadge, VersionBadge, Spinner } from "../components/common";
import { TutorialHint } from "../components/TutorialHint";
import { useAuth } from "../context/AuthContext";

export default function Telegram() {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [chatId, setChatId] = useState("");
  const [saving, setSaving] = useState(false);
  const [adminIds, setAdminIds] = useState("");
  const [savingAdmins, setSavingAdmins] = useState(false);
  const [webhook, setWebhook] = useState(null);
  const [activating, setActivating] = useState(false);
  const [versions, setVersions] = useState([]);

  const load = () => {
    api.get("/telegram/status").then((r) => {
      setStatus(r.data);
      setChatId(r.data.storage_chat_id || "");
      setAdminIds(r.data.admin_ids || "");
    }).catch((e) => toast.error(formatApiError(e)));
    api.get("/telegram/webhook-info").then((r) => setWebhook(r.data)).catch(() => setWebhook(null));
    api.get("/vod/versions").then((r) => setVersions(r.data.slice(0, 12))).catch(() => {});
  };

  useEffect(() => { load(); }, []);

  const saveChat = async () => {
    setSaving(true);
    try {
      await api.post("/telegram/config", { storage_chat_id: chatId });
      toast.success("Chat de armazenamento configurado");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const saveAdmins = async () => {
    setSavingAdmins(true);
    try {
      await api.post("/telegram/config", { admin_ids: adminIds });
      toast.success("Administradores do bot autorizados");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingAdmins(false);
    }
  };

  const activateWebhook = async () => {
    setActivating(true);
    try {
      await api.post("/telegram/webhook-setup");
      toast.success("Menu do bot ativado! Envie /start para o bot no Telegram.");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setActivating(false);
    }
  };

  if (!status) return <div className="flex justify-center py-24"><Spinner /></div>;

  return (
    <div className="space-y-6" data-testid="telegram-page">
      <PageHeader eyebrow="Infraestrutura" title="Telegram Engine" subtitle="Bot conectado via Bot API com upload fragmentado automático acima de 45 MB." />

      <TutorialHint
        id="telegram"
        title="Configurando o armazenamento e o menu do bot"
        steps={[
          "Crie um canal privado no Telegram e adicione seu bot como administrador.",
          "Descubra o chat_id do canal (ex: encaminhe uma mensagem para @userinfobot).",
          "Cole o chat_id abaixo e salve — todos os uploads do catálogo irão para lá.",
          "Autorize seu ID do Telegram em 'Administradores do bot' e clique em 'Ativar menu do bot'.",
          "No Telegram, envie /start ao bot: menu com Adicionar filme, episódio, lote, importar por link e M3U.",
          "Arquivos acima de 45 MB enviados pelo site são fragmentados automaticamente.",
        ]}
      />

      <div className="grid md:grid-cols-2 gap-5">
        <GlassCard className="p-6 anim-fade-up" data-testid="bot-status-card">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-11 h-11 rounded-xl bg-cyan-400/10 border border-cyan-400/25 flex items-center justify-center">
              <Send size={20} className="text-cyan-400" />
            </div>
            <div>
              <h3 className="font-display text-white font-semibold text-sm">Status do Bot</h3>
              <p className="text-[11px] text-slate-500">Bot API · MTProto gateway</p>
            </div>
          </div>
          {status.bot ? (
            <div className="space-y-2.5 text-sm" data-testid="bot-online-info">
              <p className="flex items-center gap-2 text-emerald-300"><CheckCircle2 size={15} /> Online</p>
              <p className="text-slate-300">@{status.bot.username} <span className="text-slate-500">({status.bot.name})</span></p>
              <p className="text-xs text-slate-500 font-mono2">Fragmentação automática: {status.chunk_size_mb} MB por chunk</p>
            </div>
          ) : (
            <p className="flex items-center gap-2 text-rose-400 text-sm" data-testid="bot-offline-info"><XCircle size={15} /> {status.bot_error || "Bot offline"}</p>
          )}
        </GlassCard>

        <GlassCard className="p-6 anim-fade-up anim-delay-1" data-testid="storage-config-card">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-11 h-11 rounded-xl bg-purple-400/10 border border-purple-400/25 flex items-center justify-center">
              <Database size={20} className="text-purple-400" />
            </div>
            <div>
              <h3 className="font-display text-white font-semibold text-sm">Canal de armazenamento</h3>
              <p className="text-[11px] text-slate-500">Destino dos arquivos do catálogo</p>
            </div>
          </div>
          {(
            <div className="flex gap-3">
              <input
                data-testid="storage-chat-input"
                className="input-dark flex-1 rounded-xl px-4 py-3 text-sm font-mono2"
                placeholder="-100xxxxxxxxxx"
                value={chatId}
                onChange={(e) => setChatId(e.target.value)}
              />
              <button data-testid="storage-save-btn" onClick={saveChat} disabled={saving || !chatId} className="btn-neon px-5 py-3 rounded-xl text-sm">
                {saving ? <Spinner /> : "Salvar"}
              </button>
            </div>
          )}
          {status.storage_chat_id && (
            <p className="mt-3 text-xs text-emerald-300 flex items-center gap-1.5" data-testid="storage-configured-msg">
              <CheckCircle2 size={13} /> Destino ativo: <span className="font-mono2">{status.storage_chat_id}</span>
            </p>
          )}
          {(
            <div className="mt-5 pt-5 border-t border-slate-800/60">
              <label className="text-[11px] font-mono2 uppercase tracking-widest text-slate-500 block mb-2">Administradores do bot (IDs do Telegram)</label>
              <div className="flex gap-3">
                <input
                  data-testid="bot-admins-input"
                  className="input-dark flex-1 rounded-xl px-4 py-3 text-sm font-mono2"
                  placeholder="123456789, 987654321"
                  value={adminIds}
                  onChange={(e) => setAdminIds(e.target.value)}
                />
                <button data-testid="bot-admins-save-btn" onClick={saveAdmins} disabled={savingAdmins} className="btn-violet px-5 py-3 rounded-xl text-sm">
                  {savingAdmins ? <Spinner /> : "Salvar"}
                </button>
              </div>
              <p className="text-[11px] text-slate-500 mt-2">Descubra seu ID falando com @userinfobot no Telegram. Sem autorização, o bot não abre o menu.</p>
            </div>
          )}
        </GlassCard>

        <GlassCard className="p-6 anim-fade-up anim-delay-2 md:col-span-2" data-testid="bot-menu-card">
          <div className="flex flex-col sm:flex-row sm:items-center gap-5 justify-between">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl bg-emerald-400/10 border border-emerald-400/25 flex items-center justify-center shrink-0">
                <BotMessageSquare size={20} className="text-emerald-400" />
              </div>
              <div>
                <h3 className="font-display text-white font-semibold text-sm">Menu interativo do bot</h3>
                <p className="text-[11px] text-slate-500 max-w-xl mt-0.5">Ative o webhook e gerencie o acervo direto no Telegram: adicionar filme/episódio, episódios em lote, importar por link ou lista M3U, ver filmes/séries e storage.</p>
              </div>
            </div>
            {(
              <button data-testid="bot-webhook-activate-btn" onClick={activateWebhook} disabled={activating} className="btn-neon px-5 py-3 rounded-xl text-sm shrink-0 flex items-center gap-2">
                {activating ? <Spinner /> : webhook?.url ? "Reativar webhook" : "Ativar menu do bot"}
              </button>
            )}
          </div>
          {webhook?.url && (
            <p className="mt-4 text-xs text-emerald-300 flex items-center gap-1.5" data-testid="webhook-active-msg">
              <CheckCircle2 size={13} /> Webhook ativo ({webhook.url}){webhook.pending_update_count > 0 && ` · ${webhook.pending_update_count} atualizações pendentes`}
              {webhook.last_error_message && <span className="text-amber-300">· último erro: {webhook.last_error_message}</span>}
            </p>
          )}
          {!webhook?.url && (
            <p className="mt-4 text-xs text-slate-500" data-testid="webhook-inactive-msg">Webhook ainda não configurado. Clique em "Ativar menu do bot" e depois envie /start para o bot no Telegram.</p>
          )}
        </GlassCard>
      </div>

      <GlassCard className="p-6 anim-fade-up anim-delay-2" data-testid="recent-uploads-card">
        <div className="flex items-center gap-3 mb-5">
          <Layers size={18} className="text-cyan-400" />
          <h3 className="font-display text-white font-semibold text-sm">Arquivos recentes</h3>
        </div>
        {versions.length === 0 ? (
          <p className="text-sm text-slate-500" data-testid="uploads-empty">Nenhum arquivo enviado ainda.</p>
        ) : (
          <div className="space-y-2" data-testid="uploads-list">
            {versions.map((v) => (
              <div key={v.id} className="flex flex-wrap items-center gap-3 p-3.5 rounded-xl bg-slate-950/50 border border-slate-800/60">
                <span className="text-sm text-white font-medium min-w-0 truncate max-w-[220px]">{v.title}</span>
                <VersionBadge version={v.version} />
                <QualityBadge quality={v.quality} />
                <div className="flex items-center gap-1 ml-auto">
                  {Array.from({ length: Math.min(v.fragments || 1, 10) }).map((_, i) => (
                    <span key={i} className="w-2 h-2 rounded-sm bg-cyan-400/70" title={`Fragmento ${i + 1}`} />
                  ))}
                  <span className="text-[10px] font-mono2 text-slate-500 ml-2">{v.fragments || 1} frag · {fmtBytes(v.size_bytes)}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </GlassCard>
    </div>
  );
}
